/** @format */
// Parent category for techniques/maneuvers.

import { store } from '@/store'
import { Base, AbilityPackage, Bonuses, ColorMap } from '@/class'
import { isUndefined } from 'vue-simple'

class Ability extends Base {
  protected missile_: string
  protected area_: string
  protected frequency_: string
  protected defend_: string
  protected enhancements_: IEnhancementData
  protected imbues_: IEnhanceData[]
  protected cost_: string
  protected speed_: number
  protected range_: string
  protected reqs_: string
  protected target_: string
  protected phase_: number
  protected trigger_: string
  protected origin_: AbilityPackage
  protected bonuses_: Bonuses
  protected material_: string
  protected damage_type_: string

  public constructor(name) {
    super(name)
    this.area_ = ''
    this.enhancements_ = null
    this.cost_ = ''
    this.desc_ = ''
    this.effect_ = ''
    this.speed_ = 0
    this.name_ = name
    this.range_ = ''
    this.reqs_ = ''
    this.special_ = ''
    this.target_ = ''
    this.origin_ = null
  }

  // ==========================================================
  // GETTERS
  // ==========================================================
  // This is an approximation, definitely not perfect
  public get TextLength() {
    return this.Effect.length + this.Special.length + this.Trigger.length + this.Cost.length + this.Range.length + (this.HasChart ? 500 : 0)
  }

  public get Area() {
    return this.area_
  }
  public get Bonuses() {
    return this.bonuses_
  }
  public get Cost() {
    return this.cost_
  }
  public get category() {
    return this.category_
  }
  public get Header() {
    var header = this.Name
    if (this.HasPhase) header += 'Phase ' + this.phase_ + ' '
    if (this.Type != 'Skill') {
      header += ' - ' + this.category_ + ' ' + this.type_
    }
    return header
  }
  public get NamelessHeader() {
    var header = ''
    if (this.HasPhase) header += 'Phase ' + this.phase_ + ' '
    if (this.Type != 'Skill') {
      header += this.category_ + ' ' + this.type_
    }
    return header
  }
  public get PhaseHeader() {
    return '**Phase ' + this.phase_ + '**'
  }
  public get Move() {
    return this.speed_
  }

  public get MomentumCost() {
    return this.CostForString('Momentum')
  }
  public get SpeedCost() {
    return this.CostForString('Speed')
  }
  public get EssenceCost() {
    return this.CostForString('Essence')
  }

  public get RangeSummary() {
    if (this.Range.length == 1 || this.Range == 'Melee') return this.Range
    if (this.Range.includes('Special')) return 'Special'
    if (this.Range.includes('Thrown')) return this.Range.replace('(_Thrown_)', ' [T]')
    return '-'
  }

  private CostForString(costString) {
    var cost = 0
    var special = this.Cost.includes('Special') || this.Cost.includes('Stance')

    for (var cost_string of this.Cost.split(',')) {
      cost_string = cost_string.trim()
      if (cost_string.includes(costString)) {
        cost += Number(cost_string[0])
        if (cost_string.includes('/')) special = true
      }
    }
    if (cost == 0) return '-'
    if (cost == 0 && special) return '*'
    return String(cost) + (special ? '*' : '')
  }

  public get Range() {
    if (this.range_.length == 0) return '-'
    return String(this.range_)
  }
  public get Reqs() {
    return this.reqs_
  }
  public get Type() {
    return this.type_
  }
  public get HasOrigin() {
    return !(this.origin_ == null)
  }
  public get Origin() {
    return this.origin_
  }
  public set Origin(origin: AbilityPackage) {
    this.origin_ = origin
  }
  public get Icon() {
    if (this.Category == 'Travel' && this.Type == 'Action' && this.Cost.includes('[+]')) return require('@/assets/Augment.svg')
    if (this.Category == 'Travel' && this.Type == 'Action' && this.Cost.includes('[-]')) return require('@/assets/Danger.svg')
    if (this.Category == 'Travel' && this.Type == 'Action') return require('@/assets/Neutral.svg')
    if (this.name_ === 'Minor Flamecraft') return require('@/assets/disciplines/Flame.svg')
    if (this.name_ === 'Minor Earthcraft') return require('@/assets/disciplines/Earth.svg')
    if (this.name_ === 'Minor Metalcraft') return require('@/assets/disciplines/Metal.svg')
    if (this.name_ === 'Minor Windcraft') return require('@/assets/disciplines/Wind.svg')
    if (this.name_ === 'Minor Watercraft') return require('@/assets/disciplines/Water.svg')
    if (this.name_ === 'Minor Woodcraft') return require('@/assets/disciplines/Wood.svg')
    if (this.category_ === 'Defensive') return require('@/assets/Defensive.svg')
    if (this.category_ === 'Offensive' || this.type_ === 'Attack') return require('@/assets/Offensive.svg')
    if (this.category_ === 'Mobility') return require('@/assets/Move.svg')
    if (this.category_ === 'Recovery') return require('@/assets/Recovery.svg')
    if (this.type_ === 'Action' || this.type_ === 'Power' || this.type_ == 'Boon' || this.type_ == 'Talent')
      return require('@/assets/disciplines/' + this.Category + '.svg')
    return require('@/assets/General.svg')
  }

  public get Color() {
    var overrideColors = ['Earth', 'Flame', 'Metal', 'Water', 'Wind', 'Wood']
    if (overrideColors.includes(this.Category)) {
      return ColorMap.get(this.Category)
    }
    if (this.Category == 'Bold') return ColorMap.get('Augment')
    if (this.Category == 'Cautious') return ColorMap.get('Danger')
    if (this.Category == 'Neutral' || this.Type == 'Camp Action') return ColorMap.get('Neutral')
    return super.Color
  }

  // ==========================================================
  // UTILITY
  // ==========================================================

  public get HasArea() {
    return this.Area != ''
  }
  public get AreaHeader() {
    return '**_Area_:** ' + this.Area
  }
  public get Enhancements() {
    return this.enhancements_
  }
  public get HasEnhancements() {
    return !(this.enhancements_ == null)
  }
  public get EnhancementsHeader() {
    var text = '**_Enhancements_:** ' + (this.enhancements_.header == undefined ? 'Choose any.' : this.enhancements_.header)
    var reactiveString = ' _[R]_'
    var exclusiveString = ' _[E]_'
    for (var enhance of this.enhancements_.enhances) {
      text +=
        '\n * **' +
        enhance.name +
        (enhance.reactive ? reactiveString : '') +
        (enhance.exclusive ? exclusiveString : '') +
        '—' +
        enhance.cost +
        ':** ' +
        enhance.effect
    }
    return text
  }
  public get EnhancementsByHeader() {
    const groupedByRole = Map.groupBy(this.enhancements_.enhances, (key) => key.cost)
    var reactiveString = ' _[R]_'
    var exclusiveString = ' _[E]_'
    var headersAndEffects = new Map<string, string[]>()
    for (var [key, enhances] of groupedByRole) {
      var text = []
      for (var enhance of enhances) {
        text.push('**' + enhance.name + '**' + (enhance.reactive ? reactiveString : '') + (enhance.exclusive ? exclusiveString : '') + ': ' + enhance.effect)
      }
      if (key == 'None') key = 'No Cost'
      headersAndEffects.set(key + ' Enhancements', text)
    }
    headersAndEffects = new Map([...headersAndEffects.entries()].sort((a, b) => (a[0] == 'None' ? -1 : b[0] == 'None' ? 1 : a[0].localeCompare(b[0]))))
    return headersAndEffects
  }
  public get ImbuesByHeader() {
    const groupedByRole = Map.groupBy(this.imbues_, (key) => key.cost)
    var reactiveString = ' _[R]_'
    var exclusiveString = ' _[E]_'
    var headersAndEffects = new Map<string, string[]>()
    for (var [key, enhances] of groupedByRole) {
      var text = []
      for (var enhance of enhances) {
        text.push('**' + enhance.name + '**' + (enhance.reactive ? reactiveString : '') + (enhance.exclusive ? exclusiveString : '') + ': ' + enhance.effect)
      }
      if (key == 'None') key = 'No Cost'
      headersAndEffects.set(key + ' Imbues', text)
    }
    headersAndEffects = new Map([...headersAndEffects.entries()].sort((a, b) => (a[0] == 'None' ? -1 : b[0] == 'None' ? 1 : a[0].localeCompare(b[0]))))
    return headersAndEffects
  }
  public get HasImbues() {
    return this.imbues_.length > 0
  }
  public get ImbuesHeader() {
    var text = '**_Imbues_:** '
    for (var imbue of this.imbues_) {
      text += '\n * **' + imbue.name + ' - ' + imbue.cost + ':** ' + imbue.effect
    }
    return text
  }
  public get HasDefend() {
    return this.defend_
  }
  public get Defend() {
    return this.defend_
  }
  public get DefendHeader() {
    return '**_Defend_:** ' + this.defend_
  }
  public get HasCost() {
    return this.cost_.length > 0 && this.cost_ != 'None'
  }
  public get CostHeader() {
    return '**_Cost_:** ' + this.cost_
  }
  public get HasFrequency() {
    return this.frequency_.length > 0
  }
  public get From() {
    if (this.origin_) return this.origin_.Name
    return '-'
  }
  public get FromPlace() {
    var count
    var place
    if (this.origin_) {
      count = this.origin_.Abilities.length
      place = this.origin_.Abilities.findIndex((x) => x.Name == this.Name)
      return 1 + place + '/' + count
    }
    return '-'
  }
  public get Frequency() {
    if (this.HasFrequency) return this.frequency_
    return '-'
  }
  public get FrequencyHeader() {
    return '**_Frequency_:** ' + this.frequency_
  }
  public get HasMove() {
    return this.speed_ > 0
  }
  public get MoveHeader() {
    return '**_Speed_:** _' + this.Move + '_'
  }
  public get HasRangeOrTarget() {
    return this.HasRange || this.HasTarget || this.material_.length > 0 || this.damage_type_.length > 0
  }
  public get HasRange() {
    return this.range_.length > 0
  }
  get RangeOrTargetHeader() {
    var header = ''
    var hasMaterial = this.material_.length > 0
    var hasDamageType = this.damage_type_.length > 0
    if (this.HasRange) header += '**Range:** _' + this.range_.replaceAll('/', '_/_').replaceAll('-', '_-_') + '_'
    if (this.HasRange && this.HasTarget) header += '; '
    if (this.HasTarget) header += this.TargetHeader
    if (hasMaterial && (this.HasRange || this.HasTarget)) header += '; '
    if (hasMaterial) {
      header += '**Material:** _' + this.material_ + '_'
    }
    if ((hasMaterial || this.HasTarget) && hasDamageType) {
      header += '; '
    }
    if (hasDamageType) {
      header += '**Damage:** _' + this.damage_type_ + '_'
    }
    return header
  }
  get RangeHeader() {
    var header = '**Range:** _' + this.range_.replaceAll('/', '_/_').replaceAll('-', '_-_') + '_'
    // if (this.HasTarget) header += ', ' + this.target_
    return header
  }
  get RangeTargetSummary() {
    var summary = ''
    if (this.HasRange) summary += '_' + this.range_.replaceAll('/', '_/_').replaceAll('-', '_-_') + '_'
    if (this.HasRange && this.HasTarget) summary += ', '
    if (this.HasTarget) summary += this.Target
    return summary
  }
  get MaterialDamageSummary() {
    var summary = ''
    var hasMaterial = this.material_.length > 0
    var hasDamageType = this.damage_type_.length > 0
    if (hasMaterial) {
      summary += this.material_
    }
    if (hasMaterial && hasDamageType) summary += ', '
    if (hasDamageType) {
      summary += this.damage_type_
    }
    return summary
  }
  public get HasMissile() {
    return this.missile_.length > 0
  }
  public get Missile() {
    return this.missile_
  }
  public get MissileHeader() {
    return '**Missile:** ' + this.missile_
  }
  public get HasReqs() {
    return this.reqs_.length > 0
  }
  public get ReqsHeader() {
    return '**Requirements:** ' + this.reqs_
  }
  public get HasTarget() {
    return this.target_.length > 0
  }
  public get Target() {
    return this.target_
  }
  public get TargetHeader() {
    return '**Target:** ' + this.target_
  }
  get HasPhase() {
    return this.phase_ != 0
  }
  public get Phase() {
    return this.phase_
  }
  get HasTrigger() {
    return this.trigger_ != ''
  }
  public get Trigger() {
    return this.trigger_
  }
  public get TriggerHeader() {
    return '**Trigger:** ' + this.trigger_
  }

  // ==========================================================
  // SERIALIZATION
  // ==========================================================

  public static Deserialize(data: IAbilityData): Ability {
    const t = new Ability(data.name)
    t.setAbilityData(data)
    return t
  }

  public setAbilityData(data: IAbilityData): void {
    this.setBaseData(data)
    this.missile_ = data.missile || ''
    this.area_ = data.area || ''
    this.enhancements_ = data.enhancements || null
    this.imbues_ = data.imbues || []
    this.defend_ = data.defend || ''
    this.cost_ = data.cost || ''
    this.speed_ = data.speed || 0
    this.frequency_ = data.frequency || ''
    this.reqs_ = data.reqs || ''
    this.phase_ = data.phase || 0
    this.target_ = data.target || ''
    this.trigger_ = data.trigger || ''
    this.range_ = data.range || ''
    this.bonuses_ = 'bonuses' in data ? Bonuses.Deserialize(data.bonuses) : new Bonuses()
    this.material_ = data.material || ''
    this.damage_type_ = data.damage_type || ''
  }
}
export default Ability
